import {
  insertUserProgramSchema,
  patchUserProgramSchema,
} from "@/api/db/schema/user-programs";
import { pushSubscriptionService } from "@/api/services/push-subscription.service";
import { userProgramService } from "@/api/services/user-program.service";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// Assign a program to the current user
export const assignProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertUserProgramSchema)
  .handler(async ({ context, data }) => {
    const userProgram = await userProgramService.assignProgram(
      context.auth.userId,
      data,
    );

    void pushSubscriptionService.sendNotificationToUser(context.auth.userId, {
      title: "Programa Katıldın!",
      body: "Antrenmanına başlamaya hazır mısın?",
      url: "/program",
    });

    return {
      status: 201,
      body: {
        message: "Program başarıyla atandı",
        userProgram,
      },
    };
  });

const joinProgramByCodeSchema = z.object({
  code: z.string(),
});

export const joinProgramByCodeFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(joinProgramByCodeSchema)
  .handler(async ({ context, data }) => {
    try {
      await userProgramService.joinProgramByCode(
        context.auth.userId,
        data.code,
      );
      return {
        status: 200,
        body: { success: true },
      };
    } catch (error) {
      return {
        status: 400,
        body: { success: false, error: (error as Error).message },
      };
    }
  });

// Update program week
const updateWeekSchema = z.object({
  userProgramId: z.string(),
  weekNumber: z.number().int().min(1),
  currentDayIndex: z.number().int().min(0).optional(),
});

export const updateProgramWeekFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(updateWeekSchema)
  .handler(async ({ context, data }) => {
    const userProgram = await userProgramService.getUserProgramById(
      data.userProgramId,
    );

    if (!userProgram || userProgram.userId !== context.auth.userId) {
      return {
        status: 403,
        body: { message: "Bu programa erişim yetkiniz yok" },
      };
    }

    const updated = await userProgramService.updateProgramWeek(
      data.userProgramId,
      data.weekNumber,
      data.currentDayIndex,
    );

    if (data.weekNumber > userProgram.currentWeek) {
      const progress = await userProgramService.getWeekProgress(
        context.auth.userId,
        userProgram.programId,
        userProgram.currentWeek,
      );

      if (progress.percentage >= 75) {
        void pushSubscriptionService.sendNotificationToUserIfEnabled(
          context.auth.userId,
          "notifWeekComplete",
          {
            title: `${userProgram.currentWeek}. Haftayı Tamamladın! 🎉`,
            body: "Harika iş! Bir sonraki haftaya geçtin.",
            url: "/program",
          },
        );
      } else {
        void pushSubscriptionService.sendNotificationToUserIfEnabled(
          context.auth.userId,
          "notifWeekComplete",
          {
            title: "Hafta Değişimi 📅",
            body: `${userProgram.currentWeek}. haftada bazı eksiklerin kaldı ama yeni haftaya geçtin. İstediğin zaman eksiklerini tamamlayabilirsin!`,
            url: "/program",
          },
        );
      }
    }

    return {
      status: 200,
      body: {
        message: "Hafta güncellendi",
        userProgram: updated,
      },
    };
  });

// Update program status
export const updateProgramStatusFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchUserProgramSchema)
  .handler(async ({ context, data }) => {
    const { userProgramId, ...updateData } = data;

    const userProgram =
      await userProgramService.getUserProgramById(userProgramId);

    if (!userProgram || userProgram.userId !== context.auth.userId) {
      return {
        status: 403,
        body: { message: "Bu programa erişim yetkiniz yok" },
      };
    }

    const updated = await userProgramService.updateProgramStatus(
      userProgramId,
      updateData,
    );

    return {
      status: 200,
      body: {
        message: "Program durumu güncellendi",
        userProgram: updated,
      },
    };
  });

// Pause program
const pauseProgramSchema = z.object({
  userProgramId: z.string(),
});

export const pauseProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(pauseProgramSchema)
  .handler(async ({ context, data }) => {
    const userProgram = await userProgramService.getUserProgramById(
      data.userProgramId,
    );

    if (!userProgram || userProgram.userId !== context.auth.userId) {
      return {
        status: 403,
        body: { message: "Bu programa erişim yetkiniz yok" },
      };
    }

    const updated = await userProgramService.pauseProgram(data.userProgramId);

    return {
      status: 200,
      body: {
        message: "Program duraklatıldı",
        userProgram: updated,
      },
    };
  });

// Complete program
const completeProgramSchema = z.object({
  userProgramId: z.string(),
});

export const completeProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(completeProgramSchema)
  .handler(async ({ context, data }) => {
    const userProgram = await userProgramService.getUserProgramById(
      data.userProgramId,
    );

    if (!userProgram || userProgram.userId !== context.auth.userId) {
      return {
        status: 403,
        body: { message: "Bu programa erişim yetkiniz yok" },
      };
    }

    const updated = await userProgramService.completeProgram(
      data.userProgramId,
    );

    return {
      status: 200,
      body: {
        message: "Program tamamlandı!",
        userProgram: updated,
      },
    };
  });

// Restart program (Reset Progress)
const restartProgramSchema = z.object({
  programId: z.string(),
});

export const restartProgramFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(restartProgramSchema)
  .handler(async ({ context, data }) => {
    const updated = await userProgramService.resetUserProgress(
      context.auth.userId,
      data.programId,
    );

    if (!updated) {
      return {
        status: 404,
        body: { message: "Program kaydı bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Program başarıyla sıfırlandı",
        userProgram: updated,
      },
    };
  });
